import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Form, Select, Typography, message } from "antd";
import { platformApi } from "../../../../../../api";
import { debounce } from "lodash";

const { Text } = Typography;

export default function LinkQuotationModal({
  handleSave,
  form,
  existQuotations,
  visibility,
  customerId,
}) {
  const [quotationData, setQuotationData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  // ── Accumulate selected objects so they survive quotationData resets ──
  const selectedObjectsRef = useRef({});

  // Ref to track selected IDs inside debounced callbacks without stale closure
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  // ── Derive mobile number: prefer quotationPhone, fallback to customer contacts ──
  const getMobileNo = (object) => {
    if (object.quotationPhone) return object.quotationPhone;
    if (object.customer && Array.isArray(object.customer.contacts)) {
      const primary = object.customer.contacts.find((c) => c.type === "Primary");
      if (primary && primary.phone) return primary.phone;
      const any = object.customer.contacts.find((c) => c.phone);
      if (any && any.phone) return any.phone;
    }
    return "No Mobile";
  };

  // ── Filter out already-linked quotations ──
  const filterExisting = (list) => {
    if (!existQuotations || !existQuotations.length) return list;
    return list.filter(
      (q) => !existQuotations.some((eq) => eq.quotationId === q.quotationId)
    );
  };

  // ── Merge incoming data into the selectedObjectsRef cache ──
  const cacheObjects = (dataArray) => {
    if (!Array.isArray(dataArray)) return;
    dataArray.forEach((item) => {
      if (item && item.id) {
        selectedObjectsRef.current[item.id] = item;
      }
    });
  };

  // ── Fetch quotations: by customer if customerId available, else general top-10 ──
  const getQuotations = () => {
    const url = customerId
      ? `/api/quotation/getCus/${customerId}`
      : `/api/quotation?limit=10`;

    platformApi
      .get(url)
      .then((result) => {
        const {
          data: {
            code = 0,
            response: { data = [] } = {},
          } = {},
        } = result || {};

        if (code === 200 && Array.isArray(data)) {
          cacheObjects(data);
          const filtered = filterExisting(data);

          setQuotationData(() => {
            // Keep currently selected items pinned at top
            const currentSelected = selectedIdsRef.current;
            const selectedItems = Object.values(selectedObjectsRef.current).filter(
              (item) => currentSelected.includes(item.id)
            );
            const selectedIdSet = new Set(selectedItems.map((i) => i.id));
            const merged = [...selectedItems];
            filtered.slice(0, 20).forEach((item) => {
              if (!selectedIdSet.has(item.id)) merged.push(item);
            });
            return merged;
          });
        } else {
          setQuotationData([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching quotations:", error);
        message.error("Unable to get quotations", 2);
      });
  };

  // ── Debounced API search ──
  const onSearchQuotation = (searchString) => {
    platformApi
      .post("/api/quotation/quotationSearch", {
        module: "quotations",
        column: "search",
        searchString,
        size: 50,
        page: 1,
        except: null,
      })
      .then((res) => {
        const {
          data: {
            response: { data: searchResults = [] } = {},
          } = {},
        } = res || {};

        if (Array.isArray(searchResults)) {
          cacheObjects(searchResults);
          setQuotationData((prevData) => {
            const currentData = Array.isArray(prevData) ? prevData : [];
            const selected = new Set(selectedIdsRef.current);
            const selectedItems = currentData.filter(
              (item) => item && selected.has(item.id)
            );
            const selectedIdSet = new Set(selectedItems.map((item) => item.id));
            const merged = [...selectedItems];
            searchResults.forEach((item) => {
              if (item && !selectedIdSet.has(item.id)) merged.push(item);
            });
            return filterExisting(merged);
          });
        }
      })
      .catch((err) => {
        console.error("Error searching quotations:", err);
      });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        const searchVal = (val || "").trim();
        if (!searchVal) {
          getQuotations();
        } else {
          onSearchQuotation(searchVal);
        }
      }, 300),
    [customerId]
  );

  const handleSearch = (value) => {
    const upperValue = (value || "").toUpperCase();
    setSearchValue(upperValue);
    debouncedSearch(upperValue);
  };

  const handleChange = (ids) => {
    setSelectedIds(ids);
    setSearchValue("");
    // Keep current quotationData intact (do NOT reload which would wipe search results)
    setQuotationData((prev) => (Array.isArray(prev) ? prev : []));
  };

  const handleBlur = () => {
    setSearchValue("");
    getQuotations();
  };

  // ── Modal open/close lifecycle ──
  useEffect(() => {
    if (visibility) {
      selectedObjectsRef.current = {};
      setSelectedIds([]);
      form.setFieldsValue({ linkQuotation: [] });
      getQuotations();
    } else {
      debouncedSearch.cancel();
      setSearchValue("");
      setSelectedIds([]);
      setQuotationData([]);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [visibility, customerId]);

  const onSave = (save = true) => {
    if (save) {
      const value = form.getFieldValue("linkQuotation");
      // Build resolved objects from accumulated cache — guaranteed to include all selected
      const resolvedObjects = (Array.isArray(value) ? value : [])
        .map((id) => selectedObjectsRef.current[id])
        .filter(Boolean);
      handleSave(value, save, resolvedObjects);
    } else {
      handleSave(undefined, false, []);
    }
    setSelectedIds([]);
    setSearchValue("");
    setQuotationData([]);
    selectedObjectsRef.current = {};
  };

  return (
    <Modal
      title="Link Quotation"
      visible={visibility}
      centered={true}
      onOk={() => onSave(true)}
      onCancel={() => onSave(false)}
      width={560}
    >
      <style>{`
        .link-quotation-select .ant-select-selection {
          border-radius: 6px !important;
          border: 1px solid #cbd5e1 !important;
          transition: all 0.2s ease-in-out;
        }
        .link-quotation-select .ant-select-selection:hover,
        .link-quotation-select .ant-select-selection:focus {
          border-color: #0284c7 !important;
          box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15) !important;
        }
        .link-quotation-select .ant-select-search__field {
          text-transform: uppercase !important;
          min-width: 250px !important;
        }
      `}</style>
      <Form>
        <Form.Item colon={false} label={<Text>Link Quotation</Text>}>
          {form.getFieldDecorator("linkQuotation", {
            rules: [{ required: false, message: "Enter Quotation" }],
          })(
            <Select
              showSearch
              showAction={["click", "focus"]}
              mode="multiple"
              placeholder="Search by quotation ID or mobile number"
              filterOption={(input, option) => {
                if (option.props.disabled || option.props.value === "no-data")
                  return false;
                const obj =
                  selectedObjectsRef.current[option.props.value] ||
                  quotationData.find((q) => q.id === option.props.value);
                if (!obj) return false;
                const mobile = getMobileNo(obj);
                const text = `${obj.quotationId || ""} ${mobile}`.toLowerCase();
                return text.includes(input.toLowerCase());
              }}
              searchValue={searchValue}
              onSearch={handleSearch}
              onChange={handleChange}
              onBlur={handleBlur}
              notFoundContent="No quotations found"
              className="link-quotation-select"
              style={{ width: "100%" }}
            >
              {Array.isArray(quotationData) && quotationData.length > 0 ? (
                quotationData.map((object) => (
                  <Select.Option key={object.id} value={object.id}>
                    {object.quotationId} - {getMobileNo(object)}
                  </Select.Option>
                ))
              ) : (
                <Select.Option disabled value="no-data">
                  No quotations available
                </Select.Option>
              )}
            </Select>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
}
